// Which modules get the Movement History flip (the header clock icon), and how
// each one's back face is set up. Rolling the flip out to another movement
// module = add its title here (+ `history` in that module's payload).

import type { HistoryViewKey } from "@bento/shared";

/**
 * How a module's history is keyed, and what the back face lets you pick:
 * - "team":     team → matches (Winner). Pick team + opponent.
 * - "teamLine": team → line → matches (Match Goals/Cards). Pick team + opponent;
 *               the line is the front face's (shown as a label).
 * - "player":   player → matches (Player Goals/Cards). Pick opponent; the
 *               player is the front face's (shown as a label).
 */
export type HistoryKind = "team" | "teamLine" | "player";

export interface MovementHistoryConfig {
  /** The module's widget key — its payload carries `bkOffsets` + `history`. */
  widgetKey:
    | "winner-1x2-movement"
    | "match-goals-movement"
    | "match-cards-movement"
    | "player-goals-movement"
    | "player-cards-movement";
  kind: HistoryKind;
  /** The view buttons, in order. Asian Handicap only suits match-result markets. */
  views: HistoryViewKey[];
  /** Line modules: the front face's default line (when none is picked yet). */
  defaultLine?: string;
  /** Player modules: which player market (for the front's default player). */
  playerKind?: "goals" | "cards";
}

const NO_AH: HistoryViewKey[] = ["odds", "rebased", "pct"];

export const MOVEMENT_HISTORY: Record<string, MovementHistoryConfig> = {
  "Winner (1x2) - Movement": {
    widgetKey: "winner-1x2-movement",
    kind: "team",
    views: ["odds", "ah", "rebased", "pct"],
  },
  "Match Goals - Movement": {
    widgetKey: "match-goals-movement",
    kind: "teamLine",
    views: NO_AH,
    defaultLine: "over_0.5",
  },
  "Match Cards - Movement": {
    widgetKey: "match-cards-movement",
    kind: "teamLine",
    views: NO_AH,
    defaultLine: "over_1.5",
  },
  "Player Goals - Movement": {
    widgetKey: "player-goals-movement",
    kind: "player",
    views: NO_AH,
    playerKind: "goals",
  },
  "Player Cards - Movement": {
    widgetKey: "player-cards-movement",
    kind: "player",
    views: NO_AH,
    playerKind: "cards",
  },
};

export function hasMovementHistory(title: string): boolean {
  return title in MOVEMENT_HISTORY;
}
